import {useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type RefObject} from "react";
import {useTranslation} from "react-i18next";
import {
  ApiError,
  OperationKey,
  confirmPhoneVerification,
  requestPhoneVerification,
  type BrowserTokens,
  type CaptchaResponse,
  type PhoneVerificationChallenge,
  type PhoneVerificationConfirmInput,
  type PhoneVerificationRequestInput,
} from "../../../shared/api/client";
import {localizeAPIError, type ErrorContext} from "../../../shared/i18n/api-errors";
import {Field} from "../../../shared/ui/field";
import {CaptchaChallenge, captchaChallengeFromError, type CaptchaChallengeDescriptor} from "./captcha-challenge";

export interface PhoneVerificationAPI {
  request(
    input: PhoneVerificationRequestInput,
    operation: OperationKey,
  ): Promise<PhoneVerificationChallenge>;
  confirm(
    input: PhoneVerificationConfirmInput,
    operation: OperationKey,
  ): Promise<void>;
}

interface PhoneVerificationProps {
  phone: string;
  tokens?: BrowserTokens;
  onVerified(tokens?: BrowserTokens): Promise<void> | void;
  api?: PhoneVerificationAPI;
  headingLevel?: "h1" | "h2";
  headingId?: string;
  allowPhoneChange?: boolean;
  autoRequest?: boolean;
  successBody?: string;
}

type TerminalState = "expired" | "exhausted" | "taken" | "success";

export function PhoneVerification({phone, tokens, onVerified, api, headingLevel = "h1", headingId, allowPhoneChange = true, autoRequest = true, successBody}: PhoneVerificationProps) {
  const {t} = useTranslation();
  const [currentPhone, setCurrentPhone] = useState(phone);
  const [phoneDraft, setPhoneDraft] = useState(phone);
  const [challenge, setChallenge] = useState<PhoneVerificationChallenge | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [captchaChallenge, setCaptchaChallenge] = useState<CaptchaChallengeDescriptor | null>(null);
  const [captchaResponse, setCaptchaResponse] = useState<CaptchaResponse | null>(null);
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const [errorContext, setErrorContext] = useState<ErrorContext>("phone-request");
  const [terminal, setTerminal] = useState<TerminalState | null>(null);
  const [clock, setClock] = useState(() => Date.now());
  const requestOperation = useRef(new OperationKey());
  const initialRequestStarted = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const localizedError = error ? localizeAPIError(error, errorContext, t) : null;
  const codeError = localizedError?.fields.code ?? (errorContext === "phone-confirm" && (error?.code === "validation" || error?.code === "client_otp_format") ? localizedError?.message : undefined);
  const Heading = headingLevel;
  const transport = useMemo(() => api ?? tokenTransport(tokens), [api, tokens]);

  const issue = useCallback(async (targetPhone: string, newOperation: boolean) => {
    if (newOperation) requestOperation.current = new OperationKey();
    setBusy(true);
    setError(null);
    setErrorContext("phone-request");
    setCaptchaResponse(null);
    if (captchaChallenge) setCaptchaAttempt((value) => value + 1);
    try {
      const next = await transport.request({phone: targetPhone}, requestOperation.current);
      setCurrentPhone(targetPhone);
      setPhoneDraft(targetPhone);
      setChallenge(next);
      setTerminal(null);
      setEditingPhone(false);
      setCode("");
      setClock(Date.now());
    } catch (caught) {
      setError(asAPIError(caught));
    } finally {
      setBusy(false);
    }
  }, [captchaChallenge, transport]);

  useEffect(() => {
    if (!autoRequest) return;
    if (initialRequestStarted.current) return;
    initialRequestStarted.current = true;
    void issue(phone, false);
  }, [autoRequest, issue, phone]);

  useEffect(() => {
    if (!challenge || terminal === "success") return;
    const timer = window.setInterval(() => {
      const now = Date.now();
      setClock(now);
      if (new Date(challenge.expires_at).getTime() <= now) {
        setCode("");
        window.clearInterval(timer);
      }
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [challenge, terminal]);

  useEffect(() => {
    if (!error) return;
    const frame = requestAnimationFrame(() => {
      const codeInput = formRef.current?.querySelector<HTMLInputElement>("input[name='code']");
      const field = formRef.current?.querySelector<HTMLElement>("[aria-invalid='true']");
      if (errorContext === "phone-confirm" && (error.code === "validation" || error.code === "client_otp_format")) {
        codeInput?.focus();
      } else {
        (field ?? errorRef.current)?.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [error, errorContext]);

  const resendSeconds = challenge ? secondsUntil(challenge.resend_after, clock) : 0;
  const expirySeconds = challenge ? secondsUntil(challenge.expires_at, clock) : 0;
  const effectiveTerminal = terminal ?? (challenge && expirySeconds === 0 ? "expired" : null);

  const submitCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^[0-9]{6}$/.test(code)) {
      setErrorContext("phone-confirm");
      setError(new ApiError(422, errorEnvelope("client_otp_format", {code: "client_otp_format"})));
      if (captchaResponse) {
        setCaptchaResponse(null);
        setCaptchaAttempt((value) => value + 1);
      }
      return;
    }
    if (captchaChallenge && !captchaResponse) return;
    setBusy(true);
    setError(null);
    setErrorContext("phone-confirm");
    try {
      // A user submission is a new logical attempt. retryNetwork inside the
      // API function reuses this key only for the transport retry.
      const input: PhoneVerificationConfirmInput = {code};
      if (captchaResponse) input.captcha = captchaResponse;
      await transport.confirm(input, new OperationKey());
    } catch (caught) {
      const nextError = asAPIError(caught);
      const nextChallenge = captchaChallengeFromError(nextError);
      if (nextChallenge) setCaptchaChallenge(nextChallenge);
      setError(nextError);
      setCode("");
      setTerminal(terminalFor(nextError.code));
      if (captchaResponse) {
        setCaptchaResponse(null);
        setCaptchaAttempt((value) => value + 1);
      }
      setBusy(false);
      return;
    }
    setCaptchaResponse(null);
    setCaptchaChallenge(null);
    setTerminal("success");
    setCode("");
    try {
      await onVerified(tokens);
    } catch (caught) {
      setErrorContext("session");
      setError(asAPIError(caught));
    } finally {
      setBusy(false);
    }
  };

  const submitPhone = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = phoneDraft.trim();
    if (!target) {
      setErrorContext("phone-request");
      setError(new ApiError(422, errorEnvelope("client_required", {phone: "client_required"})));
      return;
    }
    void issue(target, true);
  };

  const finishSession = async () => {
    setBusy(true);
    setError(null);
    setErrorContext("session");
    try {
      await onVerified(tokens);
    } catch (caught) {
      setError(asAPIError(caught));
    } finally {
      setBusy(false);
    }
  };

  if (terminal === "success") {
    return <section className="auth-complete" aria-labelledby={headingId} aria-live="polite">
      <Heading id={headingId}>{t("auth.phone.successTitle")}</Heading>
      <p>{successBody ?? t("auth.phone.successBody")}</p>
      {localizedError && <PhoneError error={localizedError} errorRef={errorRef} />}
      {error && <button className="button primary" type="button" disabled={busy} onClick={() => void finishSession()}>{t("common.retry")}</button>}
    </section>;
  }

  if (!challenge || editingPhone) {
    return <section className="auth-card phone-verification" aria-labelledby={headingId}>
      <Heading id={headingId}>{t("auth.phone.title")}</Heading>
      <p>{challenge ? t("auth.phone.changeBody") : t("auth.phone.requestBody")}</p>
      <form ref={formRef} onSubmit={submitPhone} noValidate aria-busy={busy}>
        <Field
          name="phone"
          type="tel"
          label={t("auth.phoneLabel")}
          autoComplete="tel"
          value={phoneDraft}
          onChange={(event) => setPhoneDraft(event.currentTarget.value)}
          required
          autoFocus={editingPhone || !autoRequest}
          error={localizedError?.fields.phone}
        />
        {localizedError && <PhoneError error={localizedError} errorRef={errorRef} />}
        <button className="button primary" type="submit" disabled={busy}>{busy ? t("auth.phone.requesting") : t("auth.phone.requestCode")}</button>
      </form>
    </section>;
  }

  return <section className="auth-card phone-verification" aria-labelledby={headingId}>
    <Heading id={headingId}>{t("auth.phone.codeTitle")}</Heading>
    <p>{t("auth.phone.codeBody", {phone: currentPhone})}</p>
    <form ref={formRef} onSubmit={submitCode} noValidate aria-busy={busy}>
      <Field
        name="code"
        label={t("auth.phone.codeLabel")}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        minLength={6}
        maxLength={6}
        value={code}
        onChange={(event) => setCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
        required
        autoFocus
        disabled={Boolean(effectiveTerminal) || busy}
        error={codeError}
      />
      <div className="otp-status" role="status" aria-live="polite" aria-atomic="true">
        {effectiveTerminal === "expired"
          ? t("auth.phone.expired")
          : effectiveTerminal === "exhausted"
            ? t("auth.phone.exhausted")
            : effectiveTerminal === "taken"
              ? t("auth.phone.taken")
              : t("auth.phone.expiresIn", {seconds: expirySeconds})}
      </div>
      {captchaChallenge && <CaptchaChallenge
        challenge={captchaChallenge}
        attempt={captchaAttempt}
        onResponse={setCaptchaResponse}
      />}
      {localizedError && <PhoneError error={localizedError} errorRef={errorRef} />}
      {!effectiveTerminal && <button className="button primary" type="submit" disabled={busy || (Boolean(captchaChallenge) && !captchaResponse)}>{busy ? t("auth.phone.confirming") : t("auth.phone.confirm")}</button>}
      <div className="otp-actions">
        <button
          className="button secondary"
          type="button"
          disabled={busy || resendSeconds > 0}
          onClick={() => void issue(currentPhone, true)}
        >
          {resendSeconds > 0 ? t("auth.phone.resendIn", {seconds: resendSeconds}) : t("auth.phone.resend")}
        </button>
        {effectiveTerminal === "taken" && allowPhoneChange && <button className="button secondary" type="button" disabled={busy} onClick={() => {
          setEditingPhone(true);
          setError(null);
        }}>{t("auth.phone.change")}</button>}
      </div>
    </form>
  </section>;
}

function tokenTransport(tokens?: BrowserTokens): PhoneVerificationAPI {
  if (!tokens) throw new Error("phone verification transport is required");
  return {
    request: (input, operation) => requestPhoneVerification(tokens.access_token, input, operation),
    confirm: (input, operation) => confirmPhoneVerification(tokens.access_token, input, operation),
  };
}

function PhoneError({error, errorRef}: {
  error: ReturnType<typeof localizeAPIError>;
  errorRef: RefObject<HTMLDivElement | null>;
}) {
  const {t} = useTranslation();
  return <div className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
    <p>{error.message}</p>
    {error.requestId && <small>{t("common.requestId")}: <code>{error.requestId}</code></small>}
  </div>;
}

function terminalFor(code: string): TerminalState | null {
  if (code === "not_allowed") return "exhausted";
  if (code === "not_found") return "expired";
  if (code === "already_exists") return "taken";
  return null;
}

function secondsUntil(value: string, now: number): number {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 0;
  return Math.max(0, Math.ceil((timestamp - now) / 1_000));
}

function asAPIError(caught: unknown): ApiError {
  return caught instanceof ApiError ? caught : new ApiError(0, errorEnvelope("client_error"));
}

function errorEnvelope(code: string, fields: Record<string, string> = {}) {
  return {error: {code, message: "", request_id: "", fields}};
}
